import React, { useState, useEffect, useMemo } from 'react';
import { Card } from '../components/Card';
import { Loading } from '../components/Loading';
import { itemService } from '../services/itemService';
import { saleService } from '../services/saleService';
import { financeService } from '../services/financeService';
import { dashboardService } from '../services/dashboardService';
import { expenseService } from '../services/expenseService';
import { Item, Sale } from '../types';
import { Link } from 'react-router-dom';
import { format, subMonths } from 'date-fns';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import {
  IconWarningTriangle,
  IconBox,
  IconClipboard,
  IconDownload,
  IconCloudBackup,
  IconTrendUp,
} from '../components/icons';
import logo from '../assets/logo.jpg';
import { formatCurrency } from '../utils/currency';

export const ClientDashboard: React.FC = () => {
  const [lowStockItems, setLowStockItems] = useState<Item[]>([]);
  const [outOfStockItems, setOutOfStockItems] = useState<Item[]>([]);
  const [todaySales, setTodaySales] = useState<any>(null);
  const [monthlySales, setMonthlySales] = useState<any>(null);
  const [monthlyFinance, setMonthlyFinance] = useState<any>(null);
  const [summary, setSummary] = useState<any>(null);
  const [recentSales, setRecentSales] = useState<Sale[]>([]);
  const [loading, setLoading] = useState(true);
  const [categoryPerformance, setCategoryPerformance] = useState<
    Array<{
      category: string;
      revenue: number;
      cost: number;
      profit: number;
      loss: number;
      quantity: number;
      sales: number;
    }>
  >([]);
  const [trendData, setTrendData] = useState<
    Array<{ month: string; revenue: number; profit: number }>
  >([]);
  const [topProducts, setTopProducts] = useState<
    Array<{ item_name: string; item_brand: string; profit: number; revenue: number; quantity: number }>
  >([]);
  const [lowPerformers, setLowPerformers] = useState<
    Array<{ item_name: string; item_brand: string; profit: number; revenue: number; quantity: number }>
  >([]);
  const [targets, setTargets] = useState<{ revenue: number; profit: number }>(() => {
    const stored = localStorage.getItem('dashboard_targets');
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (e) {
        return { revenue: 0, profit: 0 };
      }
    }
    return { revenue: 0, profit: 0 };
  });
  const [showTargetInputs, setShowTargetInputs] = useState(false);
  const [targetDraft, setTargetDraft] = useState(targets);
  const [todayExpenses, setTodayExpenses] = useState<{ total: number; count: number } | null>(null);
  const [monthlyExpenses, setMonthlyExpenses] = useState<{ total: number; count: number } | null>(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const today = new Date();
      const currentMonth = format(today, 'yyyy-MM');
      const categories = ['Tyre', 'Battery', 'Rims'];
      const trendMonths = Array.from({ length: 6 }).map((_, idx) => {
        const date = subMonths(today, 5 - idx);
        return { label: format(date, 'MMM yyyy'), value: format(date, 'yyyy-MM') };
      });

      const todayDate = format(today, 'yyyy-MM-dd');

      const [
        lowStockResponse,
        outOfStockResponse,
        todayResponse,
        monthlyResponse,
        financeResponse,
        summaryResponse,
        categoryResponses,
        trendResponses,
        topProfitResponse,
        lowProfitResponse,
        todayExpensesResponse,
        monthlyExpensesResponse,
      ] = await Promise.all([
        itemService.getLowStockItems(),
        itemService.getOutOfStockItems(),
        saleService.getTodaySales(),
        saleService.getMonthlySales(currentMonth),
        financeService.getMonthlyFinance(currentMonth),
        dashboardService.getSummary(),
        Promise.all(
          categories.map((cat) => financeService.getMonthlyFinance(currentMonth, cat))
        ),
        Promise.all(
          trendMonths.map((m) => financeService.getMonthlyFinance(m.value))
        ),
        saleService.getSales(1, 100, { sort_by: 'profit', sort_order: 'desc' }),
        saleService.getSales(1, 100, { sort_by: 'profit', sort_order: 'asc' }),
        expenseService.getDailyExpenses(todayDate),
        expenseService.getMonthlyExpenses(today.getFullYear(), today.getMonth() + 1),
      ]);

      if (lowStockResponse.success && lowStockResponse.data) {
        setLowStockItems(lowStockResponse.data);
      }
      if (outOfStockResponse.success && outOfStockResponse.data) {
        setOutOfStockItems(outOfStockResponse.data);
      }
      if (todayResponse.success && todayResponse.data) {
        setTodaySales(todayResponse.data);
      }
      if (monthlyResponse.success && monthlyResponse.data) {
        setMonthlySales(monthlyResponse.data);
      }
      if (financeResponse.success && financeResponse.data) {
        setMonthlyFinance(financeResponse.data);
      }
      if (summaryResponse.success && summaryResponse.data) {
        setSummary(summaryResponse.data);
        setRecentSales(summaryResponse.data.recent_sales || []);
      }

      if (todayExpensesResponse.success && todayExpensesResponse.data) {
        setTodayExpenses({
          total: todayExpensesResponse.data.total || 0,
          count: todayExpensesResponse.data.count || 0,
        });
      }

      if (monthlyExpensesResponse.success && monthlyExpensesResponse.data) {
        setMonthlyExpenses({
          total: monthlyExpensesResponse.data.total || 0,
          count: monthlyExpensesResponse.data.count || 0,
        });
      }

      if (categoryResponses) {
        const categoryStats = categories.map((cat, idx) => {
          const resp = categoryResponses[idx];
          if (resp?.success && resp.data) {
            return {
              category: cat,
              revenue: resp.data.total_revenue || 0,
              cost: resp.data.total_cost || 0,
              profit: resp.data.total_profit || 0,
              loss: resp.data.total_loss || 0,
              quantity: resp.data.total_quantity || 0,
              sales: resp.data.total_sales || 0,
            };
          }
          return {
            category: cat,
            revenue: 0,
            cost: 0,
            profit: 0,
            loss: 0,
            quantity: 0,
            sales: 0,
          };
        });
        setCategoryPerformance(categoryStats);
      }

      if (trendResponses) {
        const trend = trendResponses.map((resp, idx) => ({
          month: trendMonths[idx].label,
          revenue: resp?.success && resp.data ? resp.data.total_revenue || 0 : 0,
          profit: resp?.success && resp.data
            ? resp.data.total_profit || resp.data.profit_loss || 0
            : 0,
        }));
        setTrendData(trend);
      }

      if (topProfitResponse?.success && topProfitResponse.data) {
        const top = (topProfitResponse.data.sales || [])
          .filter((sale) => sale.profit !== undefined)
          .slice(0, 5)
          .map((sale) => ({
            item_name: sale.item_name || 'Unknown',
            item_brand: sale.item_brand || 'Unknown',
            profit: sale.profit || 0,
            revenue: sale.total_amount || 0,
            quantity: sale.quantity_sold,
          }));
        setTopProducts(top);
      }

      if (lowProfitResponse?.success && lowProfitResponse.data) {
        const low = (lowProfitResponse.data.sales || [])
          .filter((sale) => sale.profit !== undefined)
          .slice(0, 5)
          .map((sale) => ({
            item_name: sale.item_name || 'Unknown',
            item_brand: sale.item_brand || 'Unknown',
            profit: sale.profit || 0,
            revenue: sale.total_amount || 0,
            quantity: sale.quantity_sold,
          }));
        setLowPerformers(low);
      }
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    localStorage.setItem('dashboard_targets', JSON.stringify(targets));
  }, [targets]);

  const handleTargetSave = () => {
    setTargets(targetDraft);
    setShowTargetInputs(false);
  };

  const targetProgress = useMemo(() => {
    const revenueTarget = targets.revenue || 0;
    const profitTarget = targets.profit || 0;
    const revenueActual = monthlySales?.summary?.total_amount || 0;
    const profitActual = monthlyFinance?.profit_loss || 0;
    return {
      revenuePercent: revenueTarget ? Math.min((revenueActual / revenueTarget) * 100, 999) : 0,
      profitPercent: profitTarget ? Math.min((profitActual / profitTarget) * 100, 999) : 0,
      revenueActual,
      profitActual,
    };
  }, [monthlySales, monthlyFinance, targets]);

  const quickActions = [
    {
      label: 'Record Sale',
      to: '/sales',
      description: 'Quickly add a new sale entry.',
      icon: IconClipboard,
      bgClass: 'bg-blue-50 hover:bg-blue-100 border-blue-200',
      iconClass: 'text-blue-600',
    },
    {
      label: 'Add Inventory',
      to: '/inventory',
      description: 'Update item stock levels.',
      icon: IconBox,
      bgClass: 'bg-green-50 hover:bg-green-100 border-green-200',
      iconClass: 'text-green-600',
    },
    {
      label: 'Export Reports',
      to: '/reports',
      description: 'Download detailed reports.',
      icon: IconDownload,
      bgClass: 'bg-purple-50 hover:bg-purple-100 border-purple-200',
      iconClass: 'text-purple-600',
    },
    {
      label: 'Create Backup',
      to: '/admin',
      description: 'Create a database backup.',
      icon: IconCloudBackup,
      bgClass: 'bg-slate-50 hover:bg-slate-100 border-slate-200',
      iconClass: 'text-slate-600',
    },
  ];

  const renderProgressBar = (value: number, color: string) => (
    <div className="w-full bg-gray-200 rounded-full h-2">
      <div
        className={`h-2 rounded-full ${color}`}
        style={{ width: `${Math.min(value, 100)}%` }}
      />
    </div>
  );

  return (
    <div className="p-3 sm:p-4 md:p-6 space-y-4 sm:space-y-6">
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-3 sm:gap-4">
          <img
            src={logo}
            alt="Hilton Auto Spare Parts"
            className="h-10 w-10 sm:h-12 sm:w-12 rounded-full shadow-md border border-gray-200 flex-shrink-0"
          />
          <div className="min-w-0 flex-1">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Company Overview</h1>
            <p className="text-xs sm:text-sm text-gray-600">Monitor performance across sales, profit, and stock.</p>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 w-full">
          {quickActions.map((action) => {
            const Icon = action.icon;
            return (
              <Link
                key={action.label}
                to={action.to}
                className={`flex flex-col items-center justify-center px-2 py-2 sm:px-3 sm:py-2 border rounded-lg transition ${action.bgClass}`}
              >
                <Icon className={`w-5 h-5 sm:w-6 sm:h-6 mb-1 ${action.iconClass}`} />
                <span className="text-xs sm:text-sm font-semibold text-gray-800 text-center leading-tight">{action.label}</span>
                <span className="text-[10px] sm:text-xs text-gray-600 text-center mt-0.5 sm:mt-1 line-clamp-2">{action.description}</span>
              </Link>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
        <Card className="p-3 sm:p-4">
          <div className="text-center">
            <p className="text-xs sm:text-sm text-gray-600 mb-1 sm:mb-2">Total Stock Items</p>
            <p className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">{summary?.total_stock_items || 0}</p>
            <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5 sm:mt-1">{summary?.remaining_stock || 0} units</p>
          </div>
        </Card>
        <Card className="p-3 sm:p-4">
          <div className="text-center">
            <p className="text-xs sm:text-sm text-gray-600 mb-1 sm:mb-2">Sold Today</p>
            <p className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">
              {todaySales?.summary?.total_quantity || 0}
            </p>
            <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5 sm:mt-1">
              {formatCurrency(todaySales?.summary?.total_amount || 0)}
            </p>
          </div>
        </Card>
        <Card className="p-3 sm:p-4">
          <div className="text-center">
            <p className="text-xs sm:text-sm text-gray-600 mb-1 sm:mb-2">Monthly Revenue</p>
            <p className="text-xl sm:text-2xl md:text-3xl font-bold text-green-600">
              {formatCurrency(monthlySales?.summary?.total_amount || 0)}
            </p>
            <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5 sm:mt-1">
              {monthlySales?.summary?.total_sales || 0} sales
            </p>
          </div>
        </Card>
        <Card className="p-3 sm:p-4">
          <div className="text-center">
            <p className="text-xs sm:text-sm text-gray-600 mb-1 sm:mb-2">Monthly Profit</p>
            <p className={`text-xl sm:text-2xl md:text-3xl font-bold ${monthlyFinance?.is_profit ? 'text-green-600' : 'text-red-600'
              }`}>
              {monthlyFinance?.is_profit ? '+' : ''}{formatCurrency(monthlyFinance?.profit_loss || 0)}
            </p>
            <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5 sm:mt-1">
              Revenue: {formatCurrency(monthlyFinance?.total_revenue || 0)}
            </p>
          </div>
        </Card>
      </div>

      {/* Expenses Widget */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
        <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200 p-4 sm:p-5">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-xs sm:text-sm text-gray-600 mb-1">Today's Expenses</p>
              <p className="text-xl sm:text-2xl font-bold text-red-600">
                {formatCurrency(todayExpenses?.total || 0)}
              </p>
              <p className="text-[10px] sm:text-xs text-gray-600 mt-1">
                {todayExpenses?.count || 0} expense{todayExpenses?.count !== 1 ? 's' : ''}
              </p>
            </div>
            <Link
              to="/expenses"
              className="px-3 py-1.5 bg-red-600 text-white rounded-lg text-xs sm:text-sm font-medium hover:bg-red-700 transition whitespace-nowrap"
            >
              View
            </Link>
          </div>
        </Card>
        <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200 p-4 sm:p-5">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-xs sm:text-sm text-gray-600 mb-1">Monthly Expenses</p>
              <p className="text-xl sm:text-2xl font-bold text-orange-600">
                {formatCurrency(monthlyExpenses?.total || 0)}
              </p>
              <p className="text-[10px] sm:text-xs text-gray-600 mt-1">
                {monthlyExpenses?.count || 0} expense{monthlyExpenses?.count !== 1 ? 's' : ''}
              </p>
            </div>
            <Link
              to="/expenses"
              className="px-3 py-1.5 bg-orange-600 text-white rounded-lg text-xs sm:text-sm font-medium hover:bg-orange-700 transition whitespace-nowrap"
            >
              View
            </Link>
          </div>
        </Card>
        <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200 p-4 sm:p-5">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-xs sm:text-sm text-gray-600 mb-1">Net Profit (After Expenses)</p>
              <p className={`text-xl sm:text-2xl font-bold ${((monthlyFinance?.profit_loss || 0) - (monthlyExpenses?.total || 0)) >= 0
                  ? 'text-emerald-600'
                  : 'text-red-600'
                }`}>
                {formatCurrency((monthlyFinance?.profit_loss || 0) - (monthlyExpenses?.total || 0))}
              </p>
              <p className="text-[10px] sm:text-xs text-gray-600 mt-1">
                Profit - Expenses
              </p>
            </div>
            <Link
              to="/finance"
              className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-xs sm:text-sm font-medium hover:bg-emerald-700 transition whitespace-nowrap"
            >
              Details
            </Link>
          </div>
        </Card>
      </div>

      {/* Category performance */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
        {categoryPerformance.map((cat) => (
          <Card key={cat.category} title={`${cat.category} Performance`} className="p-4 sm:p-5">
            <div className="space-y-2 sm:space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs sm:text-sm text-gray-600">Revenue</span>
                <span className="text-xs sm:text-sm font-semibold text-green-600">{formatCurrency(cat.revenue)}</span>
              </div>
              {renderProgressBar(cat.profit >= 0 ? Math.min((cat.profit / (cat.revenue || 1)) * 100, 100) : 0, 'bg-green-500')}
              <div className="grid grid-cols-2 gap-2 sm:gap-3 text-[10px] sm:text-xs text-gray-600">
                <div>
                  <p className="font-medium text-gray-800">Profit</p>
                  <p className="text-sm text-green-600">{formatCurrency(cat.profit)}</p>
                </div>
                <div>
                  <p className="font-medium text-gray-800">Cost</p>
                  <p className="text-sm text-gray-700">{formatCurrency(cat.cost)}</p>
                </div>
                <div>
                  <p className="font-medium text-gray-800">Loss</p>
                  <p className="text-sm text-red-600">{formatCurrency(cat.loss)}</p>
                </div>
                <div>
                  <p className="font-medium text-gray-800">Units Sold</p>
                  <p className="text-sm text-gray-700">{cat.quantity}</p>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 md:gap-6">
        <Card title="Sales & Profit Trend (Last 6 Months)" className="p-3 sm:p-4 md:p-5">
          {trendData.length === 0 ? (
            <Loading text="Loading trend data..." />
          ) : (
            <div className="h-48 sm:h-56 md:h-64 -mx-2 sm:-mx-3 md:-mx-4">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trendData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={60} />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={(value) => `Ksh ${(value / 1000).toFixed(0)}k`} width={50} />
                  <Tooltip formatter={(value: number) => [formatCurrency(Math.round(value)), '']} />
                  <Legend wrapperStyle={{ fontSize: '12px' }} />
                  <Line type="monotone" dataKey="revenue" stroke="#10B981" strokeWidth={2} dot={{ r: 3 }} name="Revenue" />
                  <Line type="monotone" dataKey="profit" stroke="#3B82F6" strokeWidth={2} dot={{ r: 3 }} name="Profit" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>

        <Card title="Targets vs Actual" className="p-3 sm:p-4 md:p-5">
          <div className="space-y-3 sm:space-y-4">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-xs sm:text-sm font-semibold text-gray-800">This Month</h3>
              <button
                className="text-xs sm:text-sm text-blue-600 hover:text-blue-800 whitespace-nowrap"
                onClick={() => {
                  setShowTargetInputs((prev) => !prev);
                  setTargetDraft(targets);
                }}
              >
                {showTargetInputs ? 'Cancel' : 'Edit Targets'}
              </button>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs sm:text-sm text-gray-600 gap-2">
                <span className="flex-shrink-0">Revenue</span>
                <span className="text-right text-[10px] sm:text-xs">
                  {formatCurrency(targetProgress.revenueActual)} /{' '}
                  {targets.revenue ? formatCurrency(targets.revenue) : 'No target'}
                </span>
              </div>
              {renderProgressBar(targetProgress.revenuePercent, 'bg-blue-500')}
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs sm:text-sm text-gray-600 gap-2">
                <span className="flex-shrink-0">Profit</span>
                <span className="text-right text-[10px] sm:text-xs">
                  {formatCurrency(targetProgress.profitActual)} /{' '}
                  {targets.profit ? formatCurrency(targets.profit) : 'No target'}
                </span>
              </div>
              {renderProgressBar(targetProgress.profitPercent, 'bg-green-500')}
            </div>

            {showTargetInputs && (
              <div className="space-y-3 border-t pt-3">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700">Revenue Target (Ksh)</label>
                  <input
                    type="number"
                    value={targetDraft.revenue}
                    onChange={(e) => setTargetDraft({ ...targetDraft, revenue: Number(e.target.value) })}
                    className="mt-1 w-full border border-gray-300 rounded-md px-2 sm:px-3 py-1.5 sm:py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700">Profit Target (Ksh)</label>
                  <input
                    type="number"
                    value={targetDraft.profit}
                    onChange={(e) => setTargetDraft({ ...targetDraft, profit: Number(e.target.value) })}
                    className="mt-1 w-full border border-gray-300 rounded-md px-2 sm:px-3 py-1.5 sm:py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    className="px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-semibold text-white bg-blue-600 rounded-md hover:bg-blue-700"
                    onClick={handleTargetSave}
                  >
                    Save Targets
                  </button>
                </div>
              </div>
            )}
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 md:gap-6">
        <Card
          title={
            <div className="flex items-center gap-2">
              <IconTrendUp className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 flex-shrink-0" />
              <span className="text-sm sm:text-base">Top Performing Products</span>
            </div>
          }
          className="p-3 sm:p-4 md:p-5"
        >
          {topProducts.length === 0 ? (
            <p className="text-xs sm:text-sm text-gray-600 text-center py-4">No sales data available.</p>
          ) : (
            <div className="space-y-2 sm:space-y-3">
              {topProducts.map((item, idx) => (
                <div key={`${item.item_name}-${idx}`} className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 border border-green-200 rounded-lg px-2 sm:px-3 py-2 bg-green-50">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs sm:text-sm font-semibold text-gray-900 truncate">{item.item_name}</p>
                    <p className="text-[10px] sm:text-xs text-gray-600 truncate">{item.item_brand}</p>
                  </div>
                  <div className="text-left sm:text-right text-[10px] sm:text-xs">
                    <p className="font-semibold text-green-600">Profit: {formatCurrency(item.profit)}</p>
                    <p className="text-gray-600">Revenue: {formatCurrency(item.revenue)}</p>
                    <p className="text-gray-500">Units: {item.quantity}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card
          title={
            <div className="flex items-center gap-2">
              <IconWarningTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-red-500 flex-shrink-0" />
              <span className="text-sm sm:text-base">Items To Review (Lowest Profit)</span>
            </div>
          }
          className="p-3 sm:p-4 md:p-5"
        >
          {lowPerformers.length === 0 ? (
            <p className="text-xs sm:text-sm text-gray-600 text-center py-4">No concerning items detected.</p>
          ) : (
            <div className="space-y-2 sm:space-y-3">
              {lowPerformers.map((item, idx) => (
                <div key={`${item.item_name}-${idx}`} className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 border border-red-200 rounded-lg px-2 sm:px-3 py-2 bg-red-50">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs sm:text-sm font-semibold text-gray-900 truncate">{item.item_name}</p>
                    <p className="text-[10px] sm:text-xs text-gray-600 truncate">{item.item_brand}</p>
                  </div>
                  <div className="text-left sm:text-right text-[10px] sm:text-xs">
                    <p className="font-semibold text-red-600">Profit: {formatCurrency(item.profit)}</p>
                    <p className="text-gray-600">Revenue: {formatCurrency(item.revenue)}</p>
                    <p className="text-gray-500">Units: {item.quantity}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 md:gap-6">
        <Card
          title={
            <div className="flex items-center gap-2">
              <IconWarningTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-red-500 flex-shrink-0" />
              <span className="text-sm sm:text-base">Out of Stock Items</span>
            </div>
          }
          headerAction={
            <Link
              to="/inventory"
              className="text-xs sm:text-sm text-blue-600 hover:text-blue-800 font-medium whitespace-nowrap"
            >
              View All →
            </Link>
          }
          className="p-3 sm:p-4 md:p-5"
        >
          {loading ? (
            <Loading text="Loading..." />
          ) : outOfStockItems.length === 0 ? (
            <p className="text-xs sm:text-sm text-gray-600 text-center py-4">No out of stock items</p>
          ) : (
            <div className="space-y-2">
              {outOfStockItems.slice(0, 5).map((item) => (
                <div
                  key={item.id}
                  className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 p-2 sm:p-3 bg-red-50 rounded-lg border border-red-200"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-xs sm:text-sm font-medium text-gray-900 truncate">{item.name}</p>
                    <p className="text-[10px] sm:text-xs text-gray-600 truncate">{item.brand}</p>
                  </div>
                  <span className="px-2 py-1 text-[10px] sm:text-xs font-semibold rounded-full bg-red-100 text-red-800 whitespace-nowrap">
                    Out of Stock
                  </span>
                </div>
              ))}
              {outOfStockItems.length > 5 && (
                <p className="text-xs sm:text-sm text-gray-600 text-center pt-2">
                  +{outOfStockItems.length - 5} more items
                </p>
              )}
            </div>
          )}
        </Card>

        <Card
          title={
            <div className="flex items-center gap-2">
              <IconWarningTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-500 flex-shrink-0" />
              <span className="text-sm sm:text-base">Running Low Items</span>
            </div>
          }
          headerAction={
            <Link
              to="/inventory"
              className="text-xs sm:text-sm text-blue-600 hover:text-blue-800 font-medium whitespace-nowrap"
            >
              View All →
            </Link>
          }
          className="p-3 sm:p-4 md:p-5"
        >
          {loading ? (
            <Loading text="Loading..." />
          ) : lowStockItems.length === 0 ? (
            <p className="text-xs sm:text-sm text-gray-600 text-center py-4">No low stock items</p>
          ) : (
            <div className="space-y-2">
              {lowStockItems.slice(0, 5).map((item) => (
                <div
                  key={item.id}
                  className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 p-2 sm:p-3 bg-yellow-50 rounded-lg border border-yellow-200"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-xs sm:text-sm font-medium text-gray-900 truncate">{item.name}</p>
                    <p className="text-[10px] sm:text-xs text-gray-600 truncate">
                      {item.brand} • Only {item.quantity} left
                    </p>
                  </div>
                  <span className="px-2 py-1 text-[10px] sm:text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800 whitespace-nowrap">
                    Running Low
                  </span>
                </div>
              ))}
              {lowStockItems.length > 5 && (
                <p className="text-xs sm:text-sm text-gray-600 text-center pt-2">
                  +{lowStockItems.length - 5} more items
                </p>
              )}
            </div>
          )}
        </Card>
      </div>

      {/* Recent Activity */}
      <Card
        title="Recent Sales Activity"
        headerAction={<Link to="/sales" className="text-xs sm:text-sm text-blue-600 hover:text-blue-800 font-medium whitespace-nowrap">View All →</Link>}
        className="p-3 sm:p-4 md:p-5"
      >
        {recentSales.length === 0 ? (
          <p className="text-xs sm:text-sm text-gray-600 text-center py-4">No recent sales</p>
        ) : (
          <div className="overflow-x-auto -mx-2 sm:-mx-3 md:-mx-4 px-2 sm:px-3 md:px-4">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 sm:px-4 md:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-2 sm:px-4 md:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase">Item</th>
                  <th className="px-2 sm:px-4 md:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">Brand</th>
                  <th className="px-2 sm:px-4 md:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase">Qty</th>
                  <th className="px-2 sm:px-4 md:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase">Amount</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {recentSales.slice(0, 10).map((sale) => (
                  <tr key={sale.id} className="hover:bg-gray-50">
                    <td className="px-2 sm:px-4 md:px-6 py-2 sm:py-4 whitespace-nowrap text-[10px] sm:text-xs md:text-sm text-gray-900">
                      {format(new Date(sale.date), 'MMM dd, yyyy')}
                    </td>
                    <td className="px-2 sm:px-4 md:px-6 py-2 sm:py-4 whitespace-nowrap text-[10px] sm:text-xs md:text-sm font-medium text-gray-900 max-w-[100px] sm:max-w-none truncate">
                      {sale.item_name || 'N/A'}
                    </td>
                    <td className="px-2 sm:px-4 md:px-6 py-2 sm:py-4 whitespace-nowrap text-[10px] sm:text-xs md:text-sm text-gray-500 hidden sm:table-cell">
                      {sale.item_brand || 'N/A'}
                    </td>
                    <td className="px-2 sm:px-4 md:px-6 py-2 sm:py-4 whitespace-nowrap text-[10px] sm:text-xs md:text-sm text-gray-500">
                      {sale.quantity_sold}
                    </td>
                    <td className="px-2 sm:px-4 md:px-6 py-2 sm:py-4 whitespace-nowrap text-[10px] sm:text-xs md:text-sm font-semibold text-gray-900">
                      {formatCurrency(sale.total_amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

export default ClientDashboard;

